import React from 'react'
import PropTypes from 'prop-types'
import { Box, Scroller, Text, ThemeProvider, Placeholder, styled } from '@deseretbook/react-ui'
import { useMediaQuery } from '@deseretbook/react-utils'
import { Logger } from '../../dbcom'
import { DeseretBook } from '../../dbcom/api'

const logger = new Logger({ context: 'product/ImageGallery' })

// Thumbnail container that applies outline if thumbnail is the current main image
const GalleryThumbnail = styled('button')(({theme, mainImage, image}) => {
  return {
    label: 'Thumbnail',
    outline: mainImage.id === image.id ?  `solid ${theme.palette.primary.main}` : '',
  }
})

/**
 * Returns component with main image and scroller with thumbnails for image gallery on product page
 * @param {number} productId id of product
 * @returns {func} Component
 */
const ImageGallery = ({ productId }) => {
  /** TODO: Due to this issue https://gitlab.com/deseretbook/dbcom/-/issues/1722
   * using the Image component causes some issues with the image fitting the container when trying to set the imageHeight and imageWidth since that is not available.
   * When that is fixed, we can then use the Image component and pass these props:
   * imageWidth={image.attachmentWidth} imageHeight={image.attachmentHeight} maxHeight={imageContainerSize}
   * but for now, we will just use the <img> tag with the maxWidth and maxHeight styles
  */
  const [isLoading, setLoading] = React.useState(true)
  const [product, setProduct] = React.useState()
  const [activeVariantId, setActiveVariantId] = React.useState()
  const [mainImage, setMainImage] = React.useState()
  const [variantImages, setVariantImages] = React.useState([])

  // For when the screen reaches desktop size
  const isDesktop = useMediaQuery({ minWidth: 1281 })
  // For when the screen reaches mobile size
  const isMobile = useMediaQuery({ maxWidth: 639 })
  // For when the screen reaches portrait tablet size
  const isTablet = useMediaQuery({ maxWidth: 767})

  /**
   * The size the image container should be based on screen size
   * smaller when mobile, larger on bigger screens
   */
  const imageContainerSize = isMobile ? 20 : 30

  // Bootstrap breakpoints, see https://getbootstrap.com/docs/4.0/layout/grid/#grid-options
  const bootstrapMdBreakpoint = useMediaQuery({ minWidth: 992 })
  const bootstrapSmBreakpoint = useMediaQuery({ minWidth: 768 })

  /**
   * To have the Scroller.Item be "full width", we have to calculate the space the image
   * will take up with the available space we have. On mobile for example we have 100vw available.
   * Since .5 is already being used by the container on the sides, we subtract 1rem, so we can do
   * "calc(100vw - 1rem)". So we have:
   *
   *   .5rem(container), 100vw-1rem(main image), .5rem(container)
   *
   * Since the "purchase-details" sidebar column uses Bootstrap still, we still have to use Bootstrap
   * columns for the "product-images" container for alignment purposes until it's refactored to React.
   * see https://gitlab.com/deseretbook/dbcom/-/blob/master/app/views/spree/products/show.html.erb#L42
   * These are the ones we are currently using and their widths, which is 100/(12/<number of columns>):
   *   col-sm-7: 58.3333333333%
   *   col-md-8: 66.6666666667%
   *
   * It also has .5 padding on the sides so we need to subtract 1rem too.
   *
   * @returns {string} width
   */
  const calculateWidth = () => {
    let width

    if (isMobile || isTablet) {
      width = "calc(100vw - 1rem)"
    } else if (bootstrapMdBreakpoint) {
      width = "calc((100vw / (12 / 8) ) - 1rem)"
    } else if (bootstrapSmBreakpoint) {
      width = "calc((100vw / (12 / 7) ) - 1rem)"
    }

    return width
  }

  /**
   * Sets varaint and product data when a new variant is selected on a product page
   *
   * @param {string} variantId - Name of action
   * @param {object} productData - Data related to action
   */
  const buildContext = (variantId, productData) => {
    const productVariants = productData && productData.variants
    const currentVariant = productVariants && productVariants.find(({ id }) => String(id) === variantId)
    const currentVariantImages = currentVariant && currentVariant.images || []

    setActiveVariantId(variantId)
    setMainImage(currentVariantImages && currentVariantImages[0])
    setVariantImages(currentVariantImages)
  }

  /**
   * Subscriber for page context updates.
   *
   * @param {string} action - Name of action
   * @param {string} data - Data related to action
   * @param {object} productResponse - Data related to action
   */
  const subscriber = (action, data, productResponse) => {
    const newVariantId = String(data.variantId)

    // We only care about changes to the variant here.
    if (action !== 'VARIANT' || newVariantId === activeVariantId) {
      return
    }

    if (newVariantId) {
      buildContext(newVariantId, productResponse)
    }
  }

  /**
   * At first initialization we want to fetch the product and subscribe to the page contenxt to know
   * what the currently selected variant is.
   */
  React.useEffect(() => {
    /**
     * Subscibe to the context observer to update the gallery when a variant is selected
     * and set the inital context.
     *
     * @param {object} productData - all current product data
     */
    const initializeSubscriber = (productData) => {
      if (window.contextObserver) {
        window.contextObserver.subscribe((action, data) => subscriber(action, data, productData))

        // Now that we're subscribed, we'll fetch the current variant data to see if one has already
        // been selected.
        const variantData = window.contextObserver.get('VARIANT')
        const newVariantId = variantData && variantData.variantId

        if (newVariantId) {
          buildContext(newVariantId, productData)
        }
      }
    }

    /**
     * Gets product data which has the images attached
     */
    const fetchProduct = async () => {
      try {
        const productResponse =  await DeseretBook.product({ id: productId }).get()

        setProduct(productResponse)
        initializeSubscriber(productResponse)
        setLoading(false)
      } catch ({ message }) {
        logger.error(`Unable to fetch product: "${message}"`)
      }
    }

    fetchProduct()

    return () => {
      if (window.contextObserver) {
        window.contextObserver.unsubscribe(subscriber)
      }
    }
  }, [])

  return (
    <ThemeProvider>
      {/* Placeholder while fetching product info */}
      {isLoading && (
        <React.Fragment>
          <Placeholder justifyContent="center" alignItems="center" paddingBottom={!isDesktop ? 2 : 0}>
            <Placeholder.Block width="60%" height={imageContainerSize}/>
          </Placeholder>
          {isDesktop && (
            <Box paddingTop={2} paddingBottom={2.5} justifyContent="center">
              {[1, 2, 3, 4, 5].map(key => (
                <Placeholder>
                  <Placeholder.Block key={key} width={6} height={6} marginHorizontal={1}/>
                </Placeholder>
              ))}
            </Box>
          )}
        </React.Fragment>
      )}
      {/* Product loaded and there are images */}
      {!isLoading && !!variantImages.length > 0 && (
        <React.Fragment>
          {/* Scroller for mobile and tablet display */}
          {!isDesktop && (
            <Scroller
              paddingBetweenItems={0}
              scrollSnapAlign="center"
              alignItems="center"
            >
            {variantImages.map(image => (
              <Scroller.Item key={image.id}>
                <Box
                  width={calculateWidth()}
                  height={imageContainerSize}
                  alignItems="center"
                  justifyContent="center"
                  paddingHorizontal={0.25}
                >
                  {variantImages.length > 1 ?
                    <img
                      src={image.detailUrl}
                      alt={(image && image.alt) || product.name}
                      style={{maxHeight: "100%", maxWidth: "100%"}}
                    />
                    :
                    <img
                      src={image.detailUrl}
                      alt={(image && image.alt) || product.name}
                      style={{maxHeight: "100%", maxWidth: "100%"}}
                    />
                  }
                </Box>
              </Scroller.Item>
            ))}
            </Scroller>
          )}
          {/* Desktop display */}
          {isDesktop && (
            <React.Fragment>
              {/* We probably won't need the maxHeight and maxWidth set here in the <Box> when the TODO mentioned above is fixed. */}
              <Box width="100%"
                height={imageContainerSize}
                maxHeight={imageContainerSize}
                maxWidth="100%"
                justifyContent="center"
                alignItems="center"
                paddingTop={1}
              >
                <img
                  src={mainImage && mainImage.detailUrl}
                  alt={(mainImage && mainImage.alt) || product.name}
                  style={{maxHeight: "100%", maxWidth: "100%"}}
                />
              </Box>
              {/* Thumbnails */}
              {variantImages.length > 1 && (
                <Box paddingTop={2} paddingHorizontal={1}>
                  <Scroller
                    paddingBetweenItems={1}
                    paddingHorizontal={1}
                    scrollSnapAlign="center"
                    alignItems="center"
                  >
                    {variantImages.map(image => (
                      <Scroller.Item key={image.id}>
                        <Box paddingTop={0.25}>
                          <GalleryThumbnail
                            image={image}
                            mainImage={mainImage}
                            onMouseOver={() => setMainImage(image)}
                            onFocus={() => setMainImage(image)}
                          >
                            <Box
                              width={6}
                              height={6}
                              alignItems="center"
                              justifyContent="center"
                            >
                              <img
                                src={image.smallUrl}
                                alt={(image && image.alt) || product.name}
                                style={{maxHeight: "100%", maxWidth: "100%"}}
                              />
                            </Box>
                          </GalleryThumbnail>
                        </Box>
                      </Scroller.Item>
                    ))}
                  </Scroller>
                </Box>
              )}
            </React.Fragment>
          )}
        </React.Fragment>
      )}
      {/* No images, show fallback. Subtracting 5rem from width to get rectangle shape */}
      {/* Remove when we have fallbacks in react-ui. */}
      {!isLoading && !variantImages.length && (
        <Box justifyContent="center" alignItems="center">
          <Box
            background="alternate"
            width={imageContainerSize - 5}
            height={imageContainerSize}
            alignItems="center"
            justifyContent="center"
            padding={1}
          >
            <Text
              alignment="center"
              color="disabled"
              style={{ userSelect: 'none' }}
            >
              {product.name}
            </Text>
          </Box>
        </Box>
      )}
    </ThemeProvider>
  )
}

ImageGallery.propTypes = {
  productId: PropTypes.number,
}

export default ImageGallery
